import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { SINK_LABEL } from '../lib/task-dispatch';
import { type TaskAttempt, type TaskDetail, useTaskAttempts } from '../lib/tasks';
import { absolute, ago } from '../lib/time';
import { RunStatePill } from './RunStatePill';

// One task in full (#1034). Presenter — the drawer owns the read. This renders
// the scoped plan the queue row summarizes away (problem, postconditions, the
// advisory file hints) plus the run pulse, and offers the two ways out the
// projection can address: the epic this task belongs to and the run's live
// timeline. Both are existing routes, deep-linked — never a second timeline.
//
// The status pill lives on the row that opened this; here the live fact is the
// RUN, which is why the pulse (not the durable status) carries the tone.

export function TaskDetailBody(props: { task: TaskDetail; token: string | null }): JSX.Element {
  const none = (): JSX.Element => <p class="task-detail__muted">{t('fleet.task_detail.none')}</p>;
  const attempts = useTaskAttempts(() => props.task.id, props.token);

  return (
    <div class="task-detail">
      <div>
        <p class="fleet-task__title">{props.task.title}</p>
        <p class="fleet-task__meta">
          <span>{props.task.repo_full_name}</span>
          <span>{t(SINK_LABEL[props.task.sink])}</span>
          <span>{t('fleet.task_created', { when: ago(props.task.created_at) })}</span>
        </p>
      </div>

      <section>
        <h3 class="task-detail__heading">{t('fleet.task_detail.problem')}</h3>
        <p class="task-detail__problem">{props.task.problem}</p>
      </section>

      <section>
        <h3 class="task-detail__heading">{t('fleet.task_detail.acceptance')}</h3>
        <Show when={props.task.acceptance_criteria.length > 0} fallback={none()}>
          <ul class="task-detail__list">
            <For each={props.task.acceptance_criteria}>{(line) => <li>{line}</li>}</For>
          </ul>
        </Show>
      </section>

      <section>
        <h3 class="task-detail__heading">{t('fleet.task_detail.files')}</h3>
        <Show when={props.task.files_touched.length > 0} fallback={none()}>
          <ul class="task-detail__list">
            <For each={props.task.files_touched}>
              {(file) => <li class="task-detail__file">{file}</li>}
            </For>
          </ul>
        </Show>
      </section>

      <Show when={props.task.forbidden.length > 0}>
        <section>
          <h3 class="task-detail__heading">{t('fleet.task_detail.forbidden')}</h3>
          <ul class="task-detail__list">
            <For each={props.task.forbidden}>
              {(file) => <li class="task-detail__file">{file}</li>}
            </For>
          </ul>
        </section>
      </Show>

      {/* #2760 — the per-task attempt ladder. Sits BELOW the existing per-task
          detail rows, marked as its own section, so this PR cannot ship a
          second timeline (the run timeline below is run-scoped and stays
          where it is). The ladder is its own row, joined by `task_id` — never
          by `run_id` alone, which is the bug it exists to fix.
          `tasks.failure_kind` carries ONE mutable classification; this
          ladder carries N, each over its own attempt's terminal `error`
          payload, re-derived through the same `classifyTaskFailure` the DTO
          is written from. */}
      <section data-testid="task-attempts-ladder">
        <h3 class="task-detail__heading">{t('fleet.task_detail.attempts')}</h3>
        <Show
          when={(attempts.data ?? []).length > 0}
          fallback={
            <Show when={!attempts.isLoading && !attempts.isError} fallback={none()}>
              <p class="task-detail__muted">{t('fleet.task_detail.attempts_none')}</p>
            </Show>
          }
        >
          <ol class="task-detail__ladder">
            <For each={attempts.data ?? []}>
              {(attempt, index) => <AttemptRow attempt={attempt} index={index()} />}
            </For>
          </ol>
        </Show>
      </section>

      {/* #2574 — when the platform stopped trying, the account is told why.
          `failure_kind` and `failure_reason` arrive together; one without the
          other is a stale read and the row will refetch. The section renders
          for every task that carries a `failure_kind` — a refused-before-claim
          task (attempt = 0) reads differently from a ladder-exhausted one (3
          attempts spent), and the api's mapping is what makes those distinct. */}
      <Show when={props.task.failure_kind !== null && props.task.failure_reason !== null}>
        <section>
          <h3 class="task-detail__heading">{t('fleet.task_detail.failure')}</h3>
          <p class="task-detail__failure">{props.task.failure_reason}</p>
        </section>
      </Show>

      <section>
        <h3 class="task-detail__heading">{t('fleet.task_detail.run')}</h3>
        <Show
          when={props.task.run}
          fallback={<p class="task-detail__muted">{t('fleet.task_detail.run_none')}</p>}
        >
          {(run) => (
            <>
              <RunStatePill state={run().state} />
              <p class="fleet-task__meta">
                <Show when={run().last_kind}>
                  {(kind) => <span>{t('fleet.task_detail.run_last', { kind: kind() })}</span>}
                </Show>
                <Show when={run().last_at}>
                  {(at) => (
                    <span>
                      <time datetime={at()} title={absolute(at())}>
                        {ago(at())}
                      </time>
                    </span>
                  )}
                </Show>
              </p>
            </>
          )}
        </Show>
      </section>

      <p class="task-detail__links">
        <Show when={props.task.plan_id}>
          {(planId) => (
            <A href={orgHref(`/fleet/plans/${planId()}`)}>{t('fleet.task_view_plan')}</A>
          )}
        </Show>
        <Show when={props.task.run_id}>
          {(runId) => <A href={orgHref(`/fleet/runs/${runId()}`)}>{t('fleet.task_view_run')}</A>}
        </Show>
      </p>
    </div>
  );
}

/**
 * One rung of the per-task attempt ladder (#2760). One row per `run_id` the
 * task has accumulated, oldest first, so the rung's position IS the attempt
 * number — which is why the number comes from the index rather than from the
 * row's `seq` (a bigserial cursor, meaningful to the read and to nobody
 * reading this drawer).
 *
 * Each attempt's `failure_kind` was RE-DERIVED server-side from THAT attempt's
 * own terminal `error` payload, through the same `classifyTaskFailure` the
 * task row's own kind is written from. Three attempts with three different
 * terminal errors therefore read as three different reasons here; a ladder
 * built from `tasks.failure_kind` — one mutable value the latest retry
 * overwrote — would render three identical rows and look right.
 */
function AttemptRow(props: { attempt: TaskAttempt; index: number }): JSX.Element {
  const outcomeKey = (): string =>
    props.attempt.outcome === 'success'
      ? 'fleet.task_detail.attempt_outcome_success'
      : props.attempt.outcome === 'failure'
        ? 'fleet.task_detail.attempt_outcome_failure'
        : 'fleet.task_detail.attempt_outcome_pending';

  return (
    <li class="task-detail__ladder-row" data-testid="task-attempt-row">
      <span class="task-detail__ladder-num">{props.index + 1}</span>
      <span class="task-detail__ladder-outcome">{t(outcomeKey())}</span>
      <Show when={props.attempt.failure_kind}>
        {(kind) => (
          <span class="task-detail__ladder-failure">
            {t('fleet.task_detail.attempt_failure', { kind: kind() })}
          </span>
        )}
      </Show>
      <time
        class="task-detail__muted"
        datetime={props.attempt.ts}
        title={absolute(props.attempt.ts)}
      >
        {ago(props.attempt.ts)}
      </time>
      <A class="task-detail__ladder-link" href={orgHref(`/fleet/runs/${props.attempt.run_id}`)}>
        {t('fleet.task_view_run')}
      </A>
    </li>
  );
}
